import React from "react";
import { Box, IconButton, Tooltip, Typography, Chip } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";

import { ReactComponent as Logo } from "../../assets/imgs/logo.svg";
import NavigationItems from "../UI/menu/navigationItems/NavigationItems";
import { versionNumber } from "../../constants/Env";
import SideBarFooter from "../sidebarFooter/SidebarFooter";
import UserProfile from "./UserProfile";
import { ContainerSidebar, ScrollNavItens } from "../UI/generalLayout/Layout.styled";

interface ISidebar {
  currentRoute: string;
  isCollapsed?: boolean;
  toggleCollapse?: () => void;
}

const Sidebar = ({ currentRoute, isCollapsed = false, toggleCollapse }: ISidebar) => {
  const rawEnv = (process.env.REACT_APP_ENV || (process.env.NODE_ENV === "development" ? "DEV" : "")).toUpperCase();
  const showEnvBadge = rawEnv && rawEnv !== "PRODUCTION";
  const isDev = rawEnv === "DEV" || rawEnv === "DEVELOPMENT";
  const envLabel = isDev ? "DEV" : "HOMOL";
  const envColor = isDev ? "#8B5CF6" : "#F97316";

  return (
    <ContainerSidebar $isCollapsed={isCollapsed}>
      {/* Top Header: Full SPM Logo + Integrated Environment Badge + Glass Hamburger Toggle */}
      <Box
        sx={{
          py: 1.2,
          px: isCollapsed ? 1 : 2,
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "space-between",
          borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
          boxSizing: "border-box",
          gap: 1,
        }}
      >
        {!isCollapsed ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, overflow: "hidden" }}>
            <Logo style={{ width: "auto", height: "auto", maxHeight: 38 }} />
            {showEnvBadge && (
              <Chip
                label={envLabel}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "10px",
                  fontWeight: 800,
                  color: "white",
                  bgcolor: envColor,
                  borderRadius: "6px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                  px: 0.5,
                  fontFamily: "Inter, sans-serif",
                }}
              />
            )}
          </Box>
        ) : null}

        {toggleCollapse && (
          <Tooltip title={isCollapsed ? "Expandir menu" : "Recolher menu"} placement="right" arrow>
            <IconButton
              onClick={toggleCollapse}
              size="small"
              sx={{
                color: "white",
                p: 0.8,
                borderRadius: "10px",
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  transform: "scale(1.05)",
                },
              }}
            >
              {isCollapsed ? <MenuIcon sx={{ fontSize: 22 }} /> : <MenuOpenIcon sx={{ fontSize: 22 }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Integrated Executive Medical Workspace Panel (Two Separate Selectors) */}
      <UserProfile isCollapsed={isCollapsed} />

      {/* Scrollable Navigation Items */}
      <ScrollNavItens isMobile={false} style={{ flex: 1, overflowY: "auto", paddingTop: "4px" }}>
        <NavigationItems currentRoute={currentRoute} isCollapsed={isCollapsed} />
      </ScrollNavItens>

      {/* Footer Section: Logout + Version Info */}
      <Box
        sx={{
          pb: 1.5,
          pt: 1,
          px: isCollapsed ? 0.5 : 1.5,
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 0.8,
          borderTop: "1px solid rgba(255, 255, 255, 0.12)",
        }}
      >
        <SideBarFooter isCollapsed={isCollapsed} />

        {!isCollapsed && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.55)",
              fontSize: "0.7rem",
              mt: 0.2,
              fontFamily: "Inter, sans-serif",
            }}
          >
            v{versionNumber}
          </Typography>
        )}
      </Box>
    </ContainerSidebar>
  );
};

export default Sidebar;
